import type { ReactNode } from 'react';
import { Mail } from 'lucide-react';
import { useSite } from '@/app/site-context';
import { InquiryForm } from '@/components/marketing/InquiryForm';
import { RotatingArtwork } from '@/components/marketing/RotatingArtwork';
import { Card, ExternalButtonLink } from '@/components/ui';

/**
 * The five illustrations, cross-fading on a timer — the same rotation the marketing hero
 * runs (see `RotatingArtwork`), for the same reason: the range of the artwork should be
 * visible to a visitor who simply reads the page, not only to one who comes back to it.
 *
 * The ground of each is cut to transparent — a connected flood from the border through
 * near-white, not a luminance threshold, because the illustrations contain white shirts,
 * kanduras and trainers that a per-pixel rule would punch holes straight through. So they
 * sit directly on the section with no frame and nothing to line up, and two of them can
 * overlap mid-fade without either one's ground showing.
 *
 * All five are fitted (never cropped) onto one 1536x1024 canvas, so which one the
 * rotation shows can never change the column's height. `BANNER` is that shared canvas.
 *
 * These same five files also appear in the hero's rotation (see `HeroImage`), which
 * reserves a different frame — 1672x941. Re-framing one of them in place to suit this
 * column would therefore move it against the hero's set too; add a sibling rather than
 * overwriting, or normalise for both.
 */
const BANNER_NAMES = [
  '/contact-banner-art',
  '/contact-banner-art2',
  '/contact-banner-art3',
  '/contact-banner-art4',
  '/contact-banner-art5',
];

const BANNER = { width: 1536, height: 1024 } as const;

interface ContactBlockProps {
  /** Recorded against the enquiry, so the homepage and /contact are distinguishable. */
  sourcePath: string;
  /**
   * Namespaces the form's control ids.
   *
   * Two instances never share a page today, but the ids are global to the document and a
   * duplicate `<label for>` silently focuses the wrong input, so the caller names it.
   */
  idPrefix: string;
  /**
   * Whether the banner may be deferred.
   *
   * `false` on /contact, where the artwork is above the fold once the hero scrolls and a
   * late swap-in next to a form the visitor is already typing into reads as a jump. The
   * homepage block sits several screens down, where lazy is simply correct.
   */
  lazyImage?: boolean;
}

/**
 * Illustration on the left, enquiry form on the right.
 *
 * One component rather than two arrangements of the same parts, because /contact and the
 * homepage's closing block are meant to be the same design — and the previous pair, a
 * framed banner on one page and a near-black split card on the other, had already
 * drifted into looking like two different sites.
 */
export function ContactBlock({
  sourcePath,
  idPrefix,
  lazyImage = true,
}: ContactBlockProps): ReactNode {
  const { settings } = useSite();

  const email = settings.contact.email;

  return (
    /*
      `items-start`, not `items-stretch`: the artwork has a fixed aspect ratio and the
      form's height depends on how many validation messages are showing, so tying the two
      columns together would either crop the illustration or leave it floating in a column
      taller than itself. The image then centres itself against the taller form.

      `grid-cols-1` below `lg` is load-bearing. With no columns declared, the grid's one
      implicit column is `auto`, which never shrinks below its widest item's min-content —
      about 357px here — so on a 360px phone the block pushed past the gutter and made the
      whole page wider than the screen. Android Chrome then zooms the page out to fit it,
      which is what shifted every section left and the loader right. `grid-cols-1` is
      `minmax(0, 1fr)`: the column is the width it is given, and the content wraps.
    */
    <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:gap-12">
      <div className="lg:self-center">
        <RotatingArtwork
          names={BANNER_NAMES}
          width={BANNER.width}
          height={BANNER.height}
          eager={!lazyImage}
        />

        {/*
          The lockup the artwork used to carry, set in the page's own type.

          Not an `h2` — the section already has one, and a second at the same level would
          claim this illustration is a new topic rather than a caption for the form beside
          it. `p` with heading-sized type says what it is: a strapline.
        */}
        <p className="mt-4 text-center text-[30px] font-bold leading-[1.15] tracking-tight text-heading md:text-[38px]">
          Great talent.{' '}
          <span className="text-gradient bg-gradient-to-r from-primary-strong to-primary">
            Anytime. Anywhere.
          </span>
        </p>
        <p className="mx-auto mt-3 max-w-[30rem] text-center text-[15px] leading-relaxed text-body">
          A boutique search firm that personally finds, evaluates and places exceptional talent for
          your most important roles.
        </p>
      </div>

      <Card>
        <InquiryForm
          sourcePath={sourcePath}
          idPrefix={idPrefix}
          secondaryAction={
            email ? (
              /*
                A real `mailto:`, so it opens whatever the visitor actually uses rather
                than assuming a webmail provider. The subject is prefilled because an
                untitled message from a stranger is the one most likely to be missed;
                the body is left alone, since prefilled body text tends to get sent
                verbatim.
              */
              <ExternalButtonLink
                variant="secondary"
                href={`mailto:${email}?subject=${encodeURIComponent('Hiring enquiry')}`}
                className="gap-2"
              >
                <Mail aria-hidden="true" className="size-4" />
                Email us
              </ExternalButtonLink>
            ) : null
          }
        />
      </Card>
    </div>
  );
}
